import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';

const TravelPreferences = ({ data, onUpdate, isEditing, setIsEditing }) => {
  const [localData, setLocalData] = useState(data);

  const destinationOptions = [
    'Cultural Heritage', 'Adventure', 'Beach', 'Mountains', 'Urban Exploration',
    'Wildlife', 'Historical Sites', 'Art & Museums', 'Food & Wine', 'Photography'
  ];

  const activityOptions = [
    'Museums', 'Hiking', 'Food Tours', 'Photography', 'Wildlife',
    'Water Sports', 'Cultural Shows', 'Shopping', 'Spa & Wellness', 'Nightlife'
  ];

  const accommodationTypes = [
    'Luxury Hotels', 'Mid-range Hotels', 'Budget Hotels', 'Boutique Hotels',
    'Resorts', 'Hostels', 'Vacation Rentals', 'Bed & Breakfast'
  ];

  const travelStyles = [
    'Solo Travel', 'Group Tours', 'Family Travel', 'Couple Travel',
    'Adventure Tours', 'Cultural Tours', 'Luxury Travel', 'Budget Travel'
  ];

  const accessibilityOptions = [
    'Wheelchair Access', 'Hearing Assistance', 'Visual Assistance',
    'Mobility Support', 'Dietary Requirements', 'Medical Support'
  ];

  const dietaryOptions = [
    'No Restrictions', 'Vegetarian', 'Vegan', 'Halal', 'Kosher',
    'Gluten-Free', 'Lactose-Free', 'Nut Allergies', 'Other Allergies'
  ];

  const languageOptions = [
    'English', 'Spanish', 'French', 'German', 'Italian', 'Portuguese',
    'Chinese', 'Japanese', 'Korean', 'Arabic', 'Russian', 'Dutch'
  ];

  const handleEdit = () => {
    setLocalData(data);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setLocalData(data);
    setIsEditing(false);
  };

  const handleSave = () => {
    onUpdate(localData);
    setIsEditing(false);
  };

  const updateField = (field, value, nested = null) => {
    if (nested) {
      setLocalData(prev => ({
        ...prev,
        [nested]: {
          ...prev?.[nested],
          [field]: value
        }
      }));
    } else {
      setLocalData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const toggleArrayItem = (field, item) => {
    const currentArray = localData?.[field] || [];
    const updatedArray = currentArray?.includes(item)
      ? currentArray?.filter(i => i !== item)
      : [...currentArray, item];
    updateField(field, updatedArray);
  };

  const MultiSelectBox = ({ title, options, selectedItems, field, icon }) => (
    <div className="bg-card rounded-lg p-6 border">
      <h3 className="text-lg font-medium text-foreground mb-4 flex items-center">
        <Icon name={icon} size={20} className="mr-2" />
        {title}
      </h3>
      
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {options?.map((option) => (
          <label
            key={option}
            className={`flex items-center p-3 rounded-lg border cursor-pointer transition-all ${
              (selectedItems || [])?.includes(option)
                ? 'border-primary bg-primary/10 text-primary' :'border-border bg-background hover:bg-muted/50'
            } ${!isEditing ? 'cursor-not-allowed opacity-50' : ''}`}
          >
            <input
              type="checkbox"
              checked={(selectedItems || [])?.includes(option)}
              onChange={() => isEditing && toggleArrayItem(field, option)}
              disabled={!isEditing}
              className="sr-only"
            />
            <span className="text-sm font-medium">{option}</span>
          </label>
        ))}
      </div>
    </div>
  );

  return (
    <div className="space-y-8">
      {/* Section Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-foreground">Travel Preferences</h2>
          <p className="text-muted-foreground mt-1">
            Customize your travel experience with personalized preferences
          </p>
        </div>
        
        {!isEditing ? (
          <Button
            onClick={handleEdit}
            iconName="Edit"
            variant="outline"
            size="sm"
          >
            Edit
          </Button>
        ) : (
          <div className="flex space-x-2">
            <Button
              onClick={handleCancel}
              variant="ghost"
              size="sm"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              iconName="Save"
              size="sm"
            >
              Save
            </Button>
          </div>
        )}
      </div>
      {/* Budget Range */}
      <div className="bg-card rounded-lg p-6 border">
        <h3 className="text-lg font-medium text-foreground mb-4 flex items-center">
          <Icon name="DollarSign" size={20} className="mr-2" />
          Budget Preferences
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Input
            label="Minimum Budget"
            type="number"
            value={localData?.budgetRange?.min || ''}
            onChange={(e) => updateField('min', parseInt(e?.target?.value), 'budgetRange')}
            disabled={!isEditing}
          />
          
          <Input
            label="Maximum Budget"
            type="number"
            value={localData?.budgetRange?.max || ''}
            onChange={(e) => updateField('max', parseInt(e?.target?.value), 'budgetRange')}
            disabled={!isEditing}
          />
          
          <Input
            label="Currency"
            value={localData?.budgetRange?.currency || 'USD'}
            onChange={(e) => updateField('currency', e?.target?.value, 'budgetRange')}
            disabled={!isEditing}
          />
        </div>
      </div>
      {/* Travel Style & Accommodation */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="bg-card rounded-lg p-6 border">
          <h3 className="text-lg font-medium text-foreground mb-4 flex items-center">
            <Icon name="Plane" size={20} className="mr-2" />
            Travel Style
          </h3>
          
          <div className="space-y-3">
            {travelStyles?.map((style) => (
              <label
                key={style}
                className={`flex items-center p-3 rounded-lg border cursor-pointer transition-all ${
                  localData?.travelStyle === style
                    ? 'border-primary bg-primary/10 text-primary' :'border-border bg-background hover:bg-muted/50'
                } ${!isEditing ? 'cursor-not-allowed opacity-50' : ''}`}
              >
                <input
                  type="radio"
                  name="travelStyle"
                  value={style}
                  checked={localData?.travelStyle === style}
                  onChange={(e) => isEditing && updateField('travelStyle', e?.target?.value)}
                  disabled={!isEditing}
                  className="sr-only"
                />
                <span className="text-sm font-medium">{style}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="bg-card rounded-lg p-6 border">
          <h3 className="text-lg font-medium text-foreground mb-4 flex items-center">
            <Icon name="Home" size={20} className="mr-2" />
            Accommodation Preference
          </h3>
          
          <div className="space-y-3">
            {accommodationTypes?.map((type) => (
              <label
                key={type}
                className={`flex items-center p-3 rounded-lg border cursor-pointer transition-all ${
                  localData?.accommodationPreference === type
                    ? 'border-primary bg-primary/10 text-primary' :'border-border bg-background hover:bg-muted/50'
                } ${!isEditing ? 'cursor-not-allowed opacity-50' : ''}`}
              >
                <input
                  type="radio"
                  name="accommodation"
                  value={type}
                  checked={localData?.accommodationPreference === type}
                  onChange={(e) => isEditing && updateField('accommodationPreference', e?.target?.value)}
                  disabled={!isEditing}
                  className="sr-only"
                />
                <span className="text-sm font-medium">{type}</span>
              </label>
            ))}
          </div>
        </div>
      </div>
      {/* Destination Interests */}
      <MultiSelectBox
        title="Destination Interests"
        options={destinationOptions}
        selectedItems={localData?.destinationInterests}
        field="destinationInterests"
        icon="Globe"
      />
      {/* Activity Types */}
      <MultiSelectBox
        title="Preferred Activities"
        options={activityOptions}
        selectedItems={localData?.activityTypes}
        field="activityTypes"
        icon="Activity"
      />
      {/* Accessibility & Dietary */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <MultiSelectBox
          title="Accessibility Requirements"
          options={accessibilityOptions}
          selectedItems={localData?.accessibilityRequirements}
          field="accessibilityRequirements"
          icon="Shield"
        />

        <MultiSelectBox
          title="Dietary Restrictions"
          options={dietaryOptions}
          selectedItems={localData?.dietaryRestrictions}
          field="dietaryRestrictions"
          icon="Coffee"
        />
      </div>
      {/* Languages & Frequency */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <MultiSelectBox
          title="Language Preferences"
          options={languageOptions}
          selectedItems={localData?.languagePreferences}
          field="languagePreferences"
          icon="MessageCircle"
        />

        <div className="bg-card rounded-lg p-6 border">
          <h3 className="text-lg font-medium text-foreground mb-4 flex items-center">
            <Icon name="Calendar" size={20} className="mr-2" />
            Travel Frequency
          </h3>
          
          <Input
            label="How often do you travel?"
            value={localData?.travelFrequency || ''}
            onChange={(e) => updateField('travelFrequency', e?.target?.value)}
            disabled={!isEditing}
            placeholder="e.g., 3-4 times per year"
          />
        </div>
      </div>
      {/* Favorite Destinations */}
      <div className="bg-card rounded-lg p-6 border">
        <h3 className="text-lg font-medium text-foreground mb-4 flex items-center">
          <Icon name="Heart" size={20} className="mr-2" />
          Favorite Destinations
        </h3>
        
        <div className="space-y-4">
          {localData?.favoriteDestinations?.map((destination, index) => (
            <div key={index} className="flex items-center space-x-4">
              <Input
                value={destination}
                onChange={(e) => {
                  const updated = [...(localData?.favoriteDestinations || [])];
                  updated[index] = e?.target?.value;
                  updateField('favoriteDestinations', updated);
                }}
                disabled={!isEditing}
                className="flex-1"
              />
              {isEditing && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    const updated = localData?.favoriteDestinations?.filter((_, i) => i !== index);
                    updateField('favoriteDestinations', updated);
                  }}
                  iconName="X"
                />
              )}
            </div>
          ))}
          
          {isEditing && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const updated = [...(localData?.favoriteDestinations || []), ''];
                updateField('favoriteDestinations', updated);
              }}
              iconName="Plus"
            >
              Add Destination
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default TravelPreferences;